import React, { useState } from 'react';
import { ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../components/CartContext';
import AddToCartModal from '../components/AddToCartModal';

export default function Shop() {
  const [showModal, setShowModal] = useState(false);
  const [addedItem, setAddedItem] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const navigate = useNavigate();
  const { addToCart, getCartTotal } = useCart();
  const [bundleDeals, setBundleDeals] = useState([]);
  const [loading, setLoading] = useState(true)
  
  React.useEffect(() => {
  async function loadProducts() {
    try {
      const base = process.env.REACT_APP_API_BASE; // CRA
      const res = await fetch(`${base}/api/products`, {
        credentials: "include",
      });

      const data = await res.json();

      // Convert backend shape -> frontend shape (do NOT touch UI)
      const mapped = (data.products || []).map((p) => ({
        id: p.id,
        name: p.name,
        price: Number(p.price),
        image: p.image,                 // URL string from DB
        description: p.description || "",
        images: p.images || [p.image],  // array of URLs
        sizes: p.sizes || ["XS","S","M","L","XL","2XL","3XL"],
      }));

      setBundleDeals(mapped);
    } catch (e) {
      console.error("Failed to load products:", e);
    } finally {
      setLoading(false);
    }
  }

  loadProducts();
  }, []);

  const ProductCard = ({ item, index }) => (
    <div 
      className="group cursor-pointer"
      onMouseEnter={() => setHoveredItem(item.id)}
      onMouseLeave={() => setHoveredItem(null)}
      onClick={() => navigate(`/product/${item.id}`)}
    >
      {/* Image Container */}
      <div className="bg-gray-300 rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 relative">
        <div className="aspect-[3/4] relative overflow-hidden">
          {/* Actual product image */}
          <img 
            src={item.image} 
            alt={item.name}
            className={`w-full h-full object-cover transition-all duration-700 ease-out relative z-10 ${
              hoveredItem === item.id ? 'scale-110' : 'scale-100'
            }`}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          
          {/* Fallback placeholder if image doesn't load */}
          <div className="absolute inset-0 flex items-center justify-center bg-gray-300 z-0">
            <div className="w-8 h-8 sm:w-12 sm:h-12 border-2 border-gray-600 rounded-full flex items-center justify-center">
              <div className="w-6 h-6 sm:w-8 sm:h-8 border-2 border-gray-600 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 sm:w-3 sm:h-3 bg-gray-600 rounded-full"></div>
              </div>
            </div>
          </div>
          
          {/* Gradient overlay on hover - hidden on mobile */}
          <div className={`hidden sm:block absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent transition-opacity duration-500 z-20 ${
            hoveredItem === item.id ? 'opacity-100' : 'opacity-0'
          }`}></div>
          
          {/* Quick view button that appears on hover - hidden on mobile */}
          <div className={`hidden sm:flex absolute inset-0 items-center justify-center transition-all duration-500 z-30 ${
            hoveredItem === item.id ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/product/${item.id}`);
              }}
              className="bg-white text-gray-900 px-6 py-2.5 rounded-full font-semibold shadow-lg hover:bg-gray-100 transform hover:scale-105 transition-all duration-300"
            >
              Choose options / Buy
            </button>
          </div>
        </div>
      </div>
      
      {/* Product Info */}
      <div className="mt-3 sm:mt-4">
        <div className="flex-1 min-w-0">
          <span className="text-gray-800 font-medium transition-colors duration-300 group-hover:text-red-500 block text-sm sm:text-base line-clamp-2">
            {item.name}
          </span>
          <span className="text-gray-600 text-xs sm:text-sm">${item.price.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#faf8f5' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 md:py-12">
        {/* Main Shop Header */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-8 sm:mb-10 md:mb-12">Shop</h1>
        
        {/* Bundle Deals Section */}
        <div className="mb-12 sm:mb-14 md:mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">Bundle Deals</h2>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {bundleDeals.map((item, index) => (
              <ProductCard key={item.id} item={item} index={index} />
            ))}
          </div>
        </div>
      </div>

      <AddToCartModal 
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        item={addedItem}
        cartTotal={getCartTotal()}
        onViewCart={() => {
          navigate('/cart');
          setShowModal(false);
        }}
        onCheckout={() => {
          navigate('/checkout');
          setShowModal(false);
        }}
      />

      {/* CSS for disabling hover effects on touch devices */}
      <style>{`
        @media (hover: none) {
          .group:hover .group-hover\\:text-red-500 {
            color: inherit;
          }
        }

        /* Line clamp utility */
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}